import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Download, Upload, ClipboardCheck, Sparkles, Gauge, FileSpreadsheet,
  CheckCircle2, AlertCircle, History,
} from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function lastNMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
    });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const TYPES = [
  { id: "hygiene",     label: "Daily Hygiene",     icon: ClipboardCheck, hint: "One sheet per food-production facility" },
  { id: "cleanliness", label: "Daily Cleanliness", icon: Sparkles,       hint: "Sheets: Production · Restroom · Storage" },
  { id: "ccp",         label: "CCP Readings",      icon: Gauge,          hint: "One sheet per CCP at the site" },
];

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function BackfillCard({ type, sites, periods, defaultSite, onImported, onError }) {
  const [siteId, setSiteId] = useState(defaultSite || "");
  const [period, setPeriod] = useState(periods[0]?.value || "");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const Icon = type.icon;

  useEffect(() => {
    if (!siteId && defaultSite) setSiteId(defaultSite);
  }, [defaultSite, siteId]);

  const downloadTemplate = async () => {
    if (!siteId || !period) { onError("Pick a site and period first"); return; }
    setBusy(true); onError("");
    try {
      const token = localStorage.getItem("vb_access_token") || "";
      const base = `${process.env.REACT_APP_BACKEND_URL}/api/v1/raw-data/export`
        + `?type=${encodeURIComponent(type.id)}`
        + `&site_id=${encodeURIComponent(siteId)}`
        + `&period_value=${encodeURIComponent(period)}`;
      // Pre-flight via axios with `responseType: 'text'`. The platform's
      // telemetry layer is happy with text responses (it only chokes on
      // arraybuffer / blob), so we can reliably surface backend errors here.
      // On success the response body is the raw XLSX bytes as a text-decoded
      // string — we ignore it and trigger a real browser download instead,
      // which (a) shows the native "save as" dialog and (b) hands the user
      // a byte-perfect file.
      try {
        await api.get(`/raw-data/export?type=${type.id}&site_id=${siteId}&period_value=${period}`, {
          responseType: "text",
          transformResponse: [(d) => d],  // keep raw text
        });
      } catch (e) {
        let msg = `Download failed (HTTP ${e?.response?.status || "?"})`;
        const raw = e?.response?.data;
        if (typeof raw === "string" && raw.length) {
          try {
            const j = JSON.parse(raw);
            if (typeof j?.detail === "string") msg = j.detail;
            else if (typeof j?.detail?.message === "string") msg = j.detail.message;
          } catch { /* not JSON */ }
        } else if (typeof e?.response?.data?.detail === "string") {
          msg = e.response.data.detail;
        }
        onError(msg);
        return;
      }
      // Trigger a real browser download — the `?auth=<token>` query param is
      // the same pattern used by `/branding/file/{path}` so a plain anchor
      // click works without an Authorization header.
      const a = document.createElement("a");
      a.href = base + `&auth=${encodeURIComponent(token)}`;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      onError(e?.message || "Failed to download template");
    } finally { setBusy(false); }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!siteId || !period) { onError("Pick a site and period first"); return; }
    setPreviewing(true); setPreviewResult(null); onError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type.id);
      fd.append("site_id", siteId);
      fd.append("period_value", period);
      const { data } = await api.post("/raw-data/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewResult(data);
      setPendingFile(file);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Preview failed";
      onError(typeof msg === "string" ? msg : "Preview failed");
    } finally { setPreviewing(false); }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setBusy(true); onError("");
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("type", type.id);
      fd.append("site_id", siteId);
      fd.append("period_value", period);
      const { data } = await api.post("/raw-data/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onImported({ kind: type.id, ...data });
      setPreviewResult(null);
      setPendingFile(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import failed";
      onError(typeof msg === "string" ? msg : "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="vb-card p-5" data-testid={`backfill-card-${type.id}`}>
      <div className="flex flex-wrap items-center gap-5">
        {/* Title */}
        <div className="flex items-center gap-3 min-w-[240px]">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0"
            style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}>
            <Icon size={22} />
          </div>
          <div>
            <h3 className="vb-heading text-lg leading-tight" style={{ color: "var(--vb-text-2)" }}>{type.label}</h3>
            <p className="text-xs opacity-60">{type.hint}</p>
          </div>
        </div>

        {/* Selectors */}
        <div className="flex flex-wrap gap-3 flex-1 min-w-[280px]">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Site</label>
            <select className="vb-input" value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              data-testid={`backfill-site-${type.id}`}>
              <option value="">— Select —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Period (month)</label>
            <select className="vb-input" value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid={`backfill-period-${type.id}`}>
              {periods.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0 self-end">
          <button className="vb-btn ghost whitespace-nowrap" onClick={downloadTemplate} disabled={busy}
            data-testid={`backfill-export-${type.id}`}>
            <Download size={16} /> Export template
          </button>
          <label className="vb-btn whitespace-nowrap cursor-pointer">
            <Upload size={16} /> {previewing ? "Reading…" : "Upload filled file"}
            <input type="file" accept=".xlsx" className="hidden" disabled={busy || previewing}
              onChange={onFilePicked} data-testid={`backfill-import-${type.id}`} />
          </label>
        </div>
      </div>

      {previewResult && (
        <div className="vb-card p-4 mt-4" style={{ background: "var(--vb-bg)", borderColor: "var(--vb-text-2)" }}
             data-testid={`backfill-preview-${type.id}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <FileSpreadsheet size={18} style={{ color: "var(--vb-text-2)" }} />
              <strong style={{ color: "var(--vb-text-2)" }}>Preview</strong>
            </div>
            <div className="flex gap-2 flex-1 min-w-[280px]">
              <Stat label="To insert" value={previewResult.inserted} good />
              <Stat label="Skipped (live data)" value={previewResult.skipped} muted />
              <Stat label="Errors" value={previewResult.errors?.length || 0} warn={previewResult.errors?.length > 0} />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button className="vb-btn whitespace-nowrap" onClick={confirmImport}
                disabled={busy || previewResult.inserted === 0}
                data-testid={`backfill-confirm-${type.id}`}>
                {busy ? "Importing…" : `Confirm import of ${previewResult.inserted} record${previewResult.inserted === 1 ? "" : "s"}`}
              </button>
              <button className="vb-btn ghost whitespace-nowrap" onClick={() => { setPreviewResult(null); setPendingFile(null); }}
                disabled={busy} data-testid={`backfill-cancel-${type.id}`}>
                Cancel
              </button>
            </div>
          </div>
          {previewResult.errors?.length > 0 && (
            <details className="text-xs mt-3" data-testid={`backfill-errors-${type.id}`}>
              <summary className="cursor-pointer font-semibold" style={{ color: "var(--vb-error)" }}>
                {previewResult.errors.length} error{previewResult.errors.length === 1 ? "" : "s"} — click to expand
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {previewResult.errors.map((er, idx) => (
                  <li key={`${er.sheet}-${er.row}-${idx}`} className="vb-pill err text-[11px] block w-full text-left">
                    [{er.sheet} · row {er.row}] {er.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, good, warn, muted }) {
  const color = warn ? "var(--vb-error)" : good ? "var(--vb-accent)" : muted ? "var(--vb-text)" : "var(--vb-text-2)";
  return (
    <div className="vb-card p-3 flex-1 min-w-[90px]" style={{ background: "white" }}>
      <div className="text-[10px] uppercase tracking-wider opacity-60 whitespace-nowrap">{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

export default function RawDataEntry() {
  const [sites, setSites] = useState([]);
  const [retroactive, setRetroactive] = useState([]);
  const [filterSite, setFilterSite] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState(null);
  const periods = useMemo(() => lastNMonths(24), []);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get("/sites");
      setSites(data.sites || []);
    } catch (e) {
      console.error("Failed to load sites", e);
    }
  }, []);

  const loadRetroactive = useCallback(async () => {
    try {
      const params = new URLSearchParams({ type: filterType });
      if (filterSite) params.append("site_id", filterSite);
      if (filterPeriod) params.append("period_value", filterPeriod);
      const { data } = await api.get(`/raw-data/retroactive?${params.toString()}`);
      setRetroactive(data.entries || []);
    } catch (e) {
      console.error("Failed to load retroactive entries", e);
    }
  }, [filterSite, filterPeriod, filterType]);

  useEffect(() => { loadSites(); }, [loadSites]);
  useEffect(() => { loadRetroactive(); }, [loadRetroactive]);

  const onImported = (res) => {
    setToast({
      kind: res.kind,
      inserted: res.inserted,
      skipped: res.skipped,
      errors: res.errors?.length || 0,
    });
    setTimeout(() => setToast(null), 6000);
    loadRetroactive();
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  return (
    <div className="vb-fade-in" data-testid="raw-data-page">
      <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>
        Raw Data Entry
      </h1>
      <p className="opacity-70 mb-6 max-w-3xl">
        Bulk-upload historical or missed data from Excel. Export the pre-filled template,
        fill in blank cells for any missing days, then re-upload. Live data is never overwritten —
        only empty slots get filled. Every imported record is tagged as <strong>retroactive</strong>
        for audit visibility.
      </p>

      {err && (
        <div className="vb-pill err mb-4 inline-flex items-center gap-2" data-testid="rd-error">
          <AlertCircle size={14} /> {err}
        </div>
      )}
      {toast && (
        <div className="mb-4 p-4 vb-card flex items-center gap-3"
          style={{ background: "#dcefd2", borderColor: "var(--vb-accent)" }}
          data-testid="rd-toast">
          <CheckCircle2 size={20} style={{ color: "var(--vb-accent)" }} />
          <div>
            <div className="font-semibold" style={{ color: "var(--vb-accent)" }}>
              Import complete — {toast.kind}
            </div>
            <div className="text-sm opacity-80">
              {toast.inserted} inserted · {toast.skipped} skipped · {toast.errors} errors
            </div>
          </div>
        </div>
      )}

      <h2 className="vb-heading text-xl mb-3" style={{ color: "var(--vb-text-2)" }}>
        Export &amp; import templates
      </h2>
      <div className="grid grid-cols-1 gap-4 mb-10">
        {TYPES.map((t) => (
          <BackfillCard
            key={t.id}
            type={t}
            sites={sites}
            periods={periods}
            defaultSite={sites[0]?.id || ""}
            onImported={onImported}
            onError={setErr}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <History size={20} style={{ color: "var(--vb-text-2)" }} />
        <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
          Retroactive entries
        </h2>
        <span className="vb-pill ml-2">{retroactive.length}</span>
      </div>

      <div className="vb-card p-4 mb-3 grid md:grid-cols-3 gap-3" data-testid="rd-filters">
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>Type</label>
          <select className="vb-input" value={filterType}
            onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All types</option>
            <option value="hygiene">Hygiene</option>
            <option value="cleanliness">Cleanliness</option>
            <option value="ccp">CCP Readings</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>Site</label>
          <select className="vb-input" value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}>
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>Period</label>
          <select className="vb-input" value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}>
            <option value="">Any period</option>
            {periods.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div className="vb-card overflow-x-auto" data-testid="rd-history-table">
        {retroactive.length === 0 ? (
          <div className="p-8 text-center opacity-60">No retroactive entries match these filters.</div>
        ) : (
          <table className="vb-table">
            <thead>
              <tr>
                <th>Imported on</th>
                <th>Record date</th>
                <th>Type</th>
                <th>Site</th>
                <th>Target</th>
                <th>Detail</th>
                <th>Uploaded by</th>
              </tr>
            </thead>
            <tbody>
              {retroactive.map((r) => (
                <tr key={r.id} data-testid={`rd-row-${r.id}`}>
                  <td className="text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="text-xs">{(r.checked_at || "").slice(0, 10)}</td>
                  <td>
                    <span className="vb-pill" style={{ textTransform: "capitalize" }}>{r.kind}</span>
                  </td>
                  <td>{siteName(r.site_id)}</td>
                  <td>{r.scope_target_name || "—"}</td>
                  <td className="text-xs">
                    {r.kind === "ccp"
                      ? <span><strong>{r.value}{r.unit}</strong></span>
                      : <span>{r.fail_count > 0
                          ? <span className="vb-pill err">{r.fail_count} fail{r.fail_count === 1 ? "" : "s"}</span>
                          : <span className="vb-pill ok">All pass</span>}</span>}
                  </td>
                  <td className="text-xs">{r.uploader || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
